/**
 * OnboardingWizard — 5-step guided setup for new users.
 *
 * Steps:
 * 1. Connect LinkedIn
 * 2. Describe Your Business
 * 3. AI Strategy Review
 * 4. Configure ICP
 * 5. Activate
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, RefreshCw, Zap, ChevronRight, Loader2, Search, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { MatrixRain } from '@/components/matrix-rain'
import LinkedInConnect from '@/features/settings/linkedin-connect'
import IcpSettings from '@/features/settings/icp-settings'
import type { IcpSettingsHandle } from '@/features/settings/icp-settings'
import { useApi } from '@/hooks/use-api'
import { DEFAULT_OFFER } from '@dealcloser/shared'
import type { OfferConfig } from '@dealcloser/shared'
import { strategyAudience } from './strategy-audience'
import { discoveryIcp } from './discovery-icp'
import type { DiscoveryIcp } from './discovery-icp'

// ============================================================
// Types
// ============================================================

type BusinessInfo = {
  readonly offer: string
  readonly target: string
  readonly biggestWin: string
  readonly offerUrl: string
}

type UrlAnalysisResult = {
  readonly company_name: string
  readonly product_name: string
  readonly value_proposition: string
  readonly target_audience: {
    readonly job_titles: readonly string[]
    readonly industries: readonly string[]
    readonly company_sizes: readonly string[]
    readonly pain_points: readonly string[]
  }
  readonly use_cases: readonly string[] // Reserved for future use
  readonly testimonials: readonly string[]
  readonly competitors: readonly string[] // Reserved for future use
  readonly pricing_model: string // Reserved for future use
  readonly confidence: number // Reserved for future use
}

type CompanySize = '1-10' | '11-50' | '51-200' | '201-500' | '500+'

type GeneratedIcp = {
  readonly jobTitles: readonly string[]
  readonly industries: readonly string[]
  readonly companySizes: readonly CompanySize[]
  readonly locations: readonly string[]
  readonly industryKeywords: string
  readonly competitorUrls: readonly string[]
  readonly painPoints: readonly string[]
  readonly buyingSignals: readonly string[]
  readonly suggestedSearchQueries: readonly string[]
}

type AutoDiscoverResult = {
  readonly leads: readonly unknown[]
  readonly total: number
  readonly credits_used: number
  readonly credits_remaining: number
}

type AiStrategy = {
  readonly strategyName: string
  readonly icpSummary: string
  readonly suggestedApproach: string
}

const TOTAL_STEPS = 5

// ============================================================
// Strategy recommendation based on user business info
// ============================================================

function generateStrategy(
  info: BusinessInfo,
  urlAnalysis?: UrlAnalysisResult | null,
  variantIndex = 0,
): AiStrategy {
  const text = `${info.offer} ${info.biggestWin}`.toLowerCase()

  // Heuristics decide which strategy fits BEST (shown first); Regenerate then
  // cycles through the alternatives via variantIndex.
  const hasSocialProof = /result|win|client|case study|grew|saved|increased|reduced|case/i.test(text)
  const hasPainPoint = /pain|problem|struggle|challenge|frustrat|difficult|fail|broken/i.test(text)
  const hasPainFromUrl = Boolean(
    urlAnalysis?.target_audience.pain_points && urlAnalysis.target_audience.pain_points.length > 0,
  )

  // Step 2's "Who you sell to" wins; the URL analysis fills in only when it is empty.
  const audience = strategyAudience(info.target, urlAnalysis?.target_audience)
  const targetDesc = audience.who
  const industryDesc = audience.industries

  const painList = hasPainFromUrl
    ? urlAnalysis!.target_audience.pain_points.slice(0, 2).join(' and ')
    : ''

  // The three distinct outreach archetypes. Regenerate rotates through them
  // so the button visibly produces a different recommendation each click.
  const socialProof = {
    strategyName: 'Social Proof + Direct Expert',
    suggestedApproach:
      `Lead with your strongest result from "${info.biggestWin || 'your work'}" to build instant credibility. Follow with a direct offer framed around how you can replicate that success for ${targetDesc}${industryDesc ? ` in ${industryDesc}` : ''}. Close with a low-friction CTA like a quick call or case study share.`,
  }
  const painAgitate = {
    strategyName: 'Pain Agitate + Solution',
    suggestedApproach:
      `Open by naming the specific pain ${targetDesc} faces${painList ? ` — like ${painList}` : ''} — don't bury the lede. Agitate it briefly (one sentence), then position your offer as the direct solution. Curiosity-gap openers work best for connection requests; save the solution pitch for the follow-up DM.`,
  }
  const curiosity = {
    strategyName: 'Curiosity Gap + Value Exchange',
    suggestedApproach:
      `Start with an observation or insight relevant to ${targetDesc}${industryDesc ? ` in ${industryDesc}` : ''} that creates an open loop — something they'll want answered. Lead with value (an insight, a resource, or a quick win) before asking for anything. This builds trust before pitching.`,
  }

  // Order so the heuristic best-fit is first; Regenerate walks the list.
  const ordered = hasSocialProof
    ? [socialProof, curiosity, painAgitate]
    : hasPainPoint || hasPainFromUrl
      ? [painAgitate, curiosity, socialProof]
      : [curiosity, socialProof, painAgitate]

  const chosen = ordered[((variantIndex % ordered.length) + ordered.length) % ordered.length]!

  return {
    strategyName: chosen.strategyName,
    icpSummary: audience.summary,
    suggestedApproach: chosen.suggestedApproach,
  }
}

// ============================================================
// Progress bar
// ============================================================

function ProgressBar({ step, total }: { readonly step: number; readonly total: number }) {
  const pct = Math.round(((step - 1) / (total - 1)) * 100)
  return (
    <div className="space-y-2">
      <div className="flex justify-between">
        <span className="eyebrow text-[var(--color-cobalt-bright)]">
          // STEP {String(step).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </span>
        <span className="eyebrow text-[var(--color-silver)]">{pct}% COMPLETE</span>
      </div>
      <div className="h-[3px] w-full overflow-hidden rounded-full bg-[rgba(255,255,255,0.08)]">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[var(--color-cobalt)] to-[var(--color-cobalt-bright)] shadow-[0_0_12px_rgba(28,63,255,0.6)] transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

// ============================================================
// Step 1: Connect LinkedIn
// ============================================================

function StepConnectLinkedIn({ onNext }: { readonly onNext: () => void }) {
  const [connected, setConnected] = useState(false)

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <span className="eyebrow">// CONNECT</span>
        <h2 className="text-2xl font-bold tracking-tight text-[var(--color-silver-bright)]">
          Connect your LinkedIn account
        </h2>
        <p className="text-sm leading-relaxed text-[var(--color-silver)]">
          Required to send outreach, messages, and post content on your behalf.
        </p>
        <p className="text-xs leading-relaxed text-[var(--color-silver)] opacity-80">
          Tip — for best results, use a LinkedIn account with a verified phone number. These accounts
          have stronger trust signals and are far less likely to encounter restrictions.
        </p>
      </div>
      <LinkedInConnect onConnected={() => setConnected(true)} />
      <div className="flex justify-end">
        <Button onClick={onNext} disabled={!connected} className="gap-2">
          Next <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

// ============================================================
// Step 2: Business description
// ============================================================

type Step2Props = {
  readonly info: BusinessInfo
  readonly onChange: (info: BusinessInfo) => void
  readonly onNext: () => void
  readonly onSkip: () => void
  readonly onAnalyze: () => void
  readonly onManualEntry: () => void
  readonly analyzing: boolean
  readonly analysisError: string | null
  readonly urlAnalyzed: boolean
  readonly manualEntry: boolean
}

function StepDescribeBusiness({
  info, onChange, onNext, onSkip, onAnalyze, onManualEntry,
  analyzing, analysisError, urlAnalyzed, manualEntry,
}: Step2Props) {
  const analyzeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fieldsRevealed = urlAnalyzed || manualEntry

  function set(field: keyof BusinessInfo, value: string) {
    onChange({ ...info, [field]: value })
  }

  // Auto-analyze on paste (300ms debounce)
  function handleUrlPaste() {
    if (analyzeTimerRef.current) clearTimeout(analyzeTimerRef.current)
    analyzeTimerRef.current = setTimeout(() => {
      if (info.offerUrl.trim() && !analyzing && !urlAnalyzed) {
        onAnalyze()
      }
    }, 300)
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Describe Your Business</h2>
        <p className="text-muted-foreground text-sm mt-1">
          {fieldsRevealed
            ? 'Review and adjust the details below.'
            : 'Paste your offer URL and we\'ll extract your business details automatically.'}
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Your offer URL {!fieldsRevealed && '— we\'ll analyze it to pre-fill everything'}
          </label>
          <Input
            type="url"
            placeholder="https://yourcompany.com/product"
            value={info.offerUrl}
            onChange={(e) => set('offerUrl', e.target.value)}
            onPaste={() => handleUrlPaste()}
            disabled={analyzing}
            autoFocus={!fieldsRevealed}
          />
        </div>

        {/* Phase 1: URL entry — show Analyze button and manual entry link */}
        {!fieldsRevealed && !analyzing && (
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={onManualEntry}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Enter details manually
            </button>
            <Button
              onClick={onAnalyze}
              disabled={!info.offerUrl.trim()}
              className="gap-2"
            >
              <Search className="h-4 w-4" />
              Analyze
            </Button>
          </div>
        )}

        {/* Analyzing spinner */}
        {analyzing && (
          <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3">
            <Loader2 className="h-4 w-4 animate-spin text-cobalt-bright" />
            <span className="text-sm text-cobalt-bright">Analyzing your offer page…</span>
          </div>
        )}

        {/* Analysis success banner */}
        {urlAnalyzed && !manualEntry && !analysisError && (
          <div className="flex items-center gap-3 rounded-lg border border-green-500/30 bg-green-500/5 px-4 py-3">
            <Sparkles className="h-4 w-4 text-emerald-300 " />
            <span className="text-sm text-emerald-300 ">
              We extracted your business details. Review and adjust as needed.
            </span>
          </div>
        )}

        {analysisError && (
          <p className="text-sm text-destructive">{analysisError}</p>
        )}

        {/* Phase 2: Reveal all fields after analysis or manual entry */}
        {fieldsRevealed && (
          <>
            <div className="space-y-2">
              <label className="text-sm font-medium">What you offer</label>
              <textarea
                rows={3}
                placeholder="We help SaaS companies reduce churn by 30% through automated customer success playbooks…"
                value={info.offer}
                onChange={(e) => set('offer', e.target.value)}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-subtle-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Who you sell to (one sentence)</label>
              <textarea
                rows={2}
                placeholder="VP of Customer Success at B2B SaaS companies with 50-500 employees…"
                value={info.target}
                onChange={(e) => set('target', e.target.value)}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-subtle-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Your biggest customer win</label>
              <textarea
                rows={3}
                placeholder="We helped Acme Corp reduce churn from 8% to 2% in 90 days, saving them $400k ARR…"
                value={info.biggestWin}
                onChange={(e) => set('biggestWin', e.target.value)}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-subtle-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
              />
            </div>
          </>
        )}
      </div>

      {/* Navigation — only show Next/Skip when fields are revealed */}
      {fieldsRevealed && (
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onSkip}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Skip for now
          </button>
          <Button onClick={onNext} className="gap-2">
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Skip link on Phase 1 (below Analyze) */}
      {!fieldsRevealed && !analyzing && (
        <div className="text-center">
          <button
            type="button"
            onClick={onSkip}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Skip for now
          </button>
        </div>
      )}
    </div>
  )
}

// ============================================================
// Step 3: AI Strategy review
// ============================================================

type Step3Props = {
  readonly info: BusinessInfo
  readonly urlAnalysis: UrlAnalysisResult | null
  readonly onNext: () => void
  readonly onSkip: () => void
}

function StepAiReview({ info, urlAnalysis, onNext, onSkip }: Step3Props) {
  const [strategy, setStrategy] = useState<AiStrategy | null>(null)
  const [loading, setLoading] = useState(true)
  // Tracks which of the rotating strategies is shown; Regenerate advances it.
  const [variantIdx, setVariantIdx] = useState(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  function load(idx: number) {
    setLoading(true)
    setStrategy(null)
    // Brief delay so the spinner reads as "regenerating" — generateStrategy
    // itself is synchronous, so without this the swap is imperceptible.
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      setStrategy(generateStrategy(info, urlAnalysis, idx))
      setLoading(false)
    }, 450)
  }

  useEffect(() => {
    load(0)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">AI Strategy Review</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Based on what you told us, here's the strategy we recommend.
        </p>
      </div>

      {urlAnalysis && (
        <Card className="border-primary/30 bg-primary/10">
          <CardContent className="pt-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-cobalt-bright">
              <Sparkles className="h-4 w-4" />
              Insights from your offer URL
            </div>
            {urlAnalysis.value_proposition && (
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Value prop:</span> {urlAnalysis.value_proposition}
              </p>
            )}
            {urlAnalysis.target_audience.job_titles.length > 0 && (
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Target roles:</span>{' '}
                {urlAnalysis.target_audience.job_titles.slice(0, 5).join(', ')}
              </p>
            )}
            {urlAnalysis.target_audience.industries.length > 0 && (
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Industries:</span>{' '}
                {urlAnalysis.target_audience.industries.join(', ')}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {loading ? <Skeleton className="h-5 w-48" /> : strategy?.strategyName}
          </CardTitle>
          <CardDescription>
            {loading ? <Skeleton className="h-4 w-64 mt-1" /> : `Target: ${strategy?.icpSummary}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{strategy?.suggestedApproach}</p>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onSkip}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Skip for now
        </button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              const next = variantIdx + 1
              setVariantIdx(next)
              load(next)
            }}
            disabled={loading}
            className="gap-2"
          >
            <RefreshCw className={['h-4 w-4', loading ? 'animate-spin' : ''].join(' ')} />
            Regenerate
          </Button>
          <Button onClick={onNext} disabled={loading} className="gap-2">
            Looks Good <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Step 4: Configure ICP
// ============================================================

type Step4Props = {
  readonly urlAnalysis: UrlAnalysisResult | null
  readonly businessInfo: BusinessInfo
  readonly onIcpGenerated: (icp: GeneratedIcp) => void
  readonly onNext: () => void
  readonly onSkip: () => void
}

function StepConfigureIcp({ urlAnalysis, businessInfo, onIcpGenerated, onNext, onSkip }: Step4Props) {
  const apiFetch = useApi()
  const icpRef = useRef<IcpSettingsHandle>(null)
  const [generatingIcp, setGeneratingIcp] = useState(false)
  const [savingIcp, setSavingIcp] = useState(false)
  const [icpDefaults, setIcpDefaults] = useState<Partial<{
    readonly jobTitles: readonly string[]
    readonly industries: readonly string[]
    readonly companySizes: readonly CompanySize[]
    readonly locations: readonly string[]
    readonly industryKeywords: string
    readonly competitorUrls: readonly string[]
  }> | undefined>(undefined)
  const [hasGenerated, setHasGenerated] = useState(false)

  async function handleNext() {
    setSavingIcp(true)
    await icpRef.current?.save()
    setSavingIcp(false)
    onNext()
  }

  useEffect(() => {
    if (!urlAnalysis || hasGenerated) return

    async function generateIcp() {
      setGeneratingIcp(true)
      const res = await apiFetch<GeneratedIcp>('/api/ai/generate-icp', {
        method: 'POST',
        body: {
          analysis: urlAnalysis,
          businessInfo: {
            offer: businessInfo.offer || undefined,
            target: businessInfo.target || undefined,
          },
        },
      })
      setGeneratingIcp(false)
      setHasGenerated(true)

      if (res.success && res.data) {
        onIcpGenerated(res.data)
        setIcpDefaults({
          jobTitles: res.data.jobTitles,
          industries: res.data.industries,
          companySizes: res.data.companySizes,
          locations: res.data.locations,
          industryKeywords: res.data.industryKeywords,
          competitorUrls: res.data.competitorUrls.length > 0
            ? [...res.data.competitorUrls, ...Array(Math.max(0, 5 - res.data.competitorUrls.length)).fill('')]
            : undefined,
        })
      }
    }

    void generateIcp()
  }, [urlAnalysis, hasGenerated])

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Configure Your ICP</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Fine-tune your Ideal Customer Profile for precision targeting.
        </p>
      </div>

      {generatingIcp && (
        <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3">
          <Loader2 className="h-4 w-4 animate-spin text-cobalt-bright" />
          <span className="text-sm text-cobalt-bright">Generating ICP suggestions from your offer URL...</span>
        </div>
      )}

      {icpDefaults && !generatingIcp && (
        <div className="flex items-center gap-3 rounded-lg border border-green-500/30 bg-green-500/5 px-4 py-3">
          <Sparkles className="h-4 w-4 text-emerald-300 " />
          <span className="text-sm text-emerald-300 ">
            We pre-filled these based on your offer URL. Adjust as needed.
          </span>
        </div>
      )}

      {generatingIcp ? (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-64 mt-1" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-3/4" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-36" />
              <Skeleton className="h-4 w-56 mt-1" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        </div>
      ) : (
        <IcpSettings ref={icpRef} defaultValues={icpDefaults} />
      )}

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={onSkip}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Skip for now
        </button>
        <Button
          onClick={() => void handleNext()}
          disabled={generatingIcp || savingIcp}
          className="gap-2"
        >
          {savingIcp ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving…
            </>
          ) : (
            <>Next <ChevronRight className="h-4 w-4" /></>
          )}
        </Button>
      </div>
    </div>
  )
}

// ============================================================
// Step 5: Activate
// ============================================================

type CompletedSteps = {
  readonly linkedIn: boolean
  readonly business: boolean
  readonly strategy: boolean
  readonly icp: boolean
}

type Step5Props = {
  readonly completedSteps: CompletedSteps
  readonly generatedIcp: GeneratedIcp | null
  readonly linkedInAccountId: string | null
}

function StepActivate({ completedSteps, generatedIcp, linkedInAccountId }: Step5Props) {
  const navigate = useNavigate()
  const apiFetch = useApi()
  const queryClient = useQueryClient()
  const [activating, setActivating] = useState(false)
  const [activated, setActivated] = useState(false)
  const [activateError, setActivateError] = useState<string | null>(null)
  const [discovering, setDiscovering] = useState(false)
  const [importing, setImporting] = useState(false)
  const [_discoverResult, setDiscoverResult] = useState<AutoDiscoverResult | null>(null)
  const [importedCount, setImportedCount] = useState(0)
  const [discoverError, setDiscoverError] = useState<string | null>(null)
  const autoDiscoverTriggered = useRef(false)

  const leadsImported = importedCount > 0

  const checklist: ReadonlyArray<{ label: string; done: boolean }> = [
    { label: 'LinkedIn account connected', done: completedSteps.linkedIn },
    { label: 'Business description provided', done: completedSteps.business },
    { label: 'AI strategy reviewed', done: completedSteps.strategy },
    { label: 'ICP configured', done: completedSteps.icp },
    { label: `AI leads discovered${leadsImported ? ` (${importedCount} added)` : ''}`, done: leadsImported },
  ]

  // Auto-discover leads on mount — runs once
  useEffect(() => {
    if (autoDiscoverTriggered.current) return
    if (!linkedInAccountId) return
    autoDiscoverTriggered.current = true
    void runAutoDiscover()
  }, [linkedInAccountId])

  async function getIcpForDiscovery(): Promise<DiscoveryIcp | null> {
    // The ICP saved in step 4 (AI suggestions plus the user's edits) wins over
    // the unsaved AI suggestions; see discoveryIcp().
    const res = await apiFetch<DiscoveryIcp>('/api/settings/icp')
    const saved = res.success && res.data ? res.data : null
    return discoveryIcp(saved, generatedIcp, completedSteps.icp)
  }

  async function runAutoDiscover() {
    const icp = await getIcpForDiscovery()
    if (!icp || !linkedInAccountId) {
      // No ICP configured — skip silently, user can discover manually later
      return
    }

    setDiscovering(true)
    setDiscoverError(null)

    const res = await apiFetch<AutoDiscoverResult>('/api/leads/auto-discover', {
      method: 'POST',
      body: {
        icp: {
          jobTitles: icp.jobTitles,
          industries: icp.industries,
          companySizes: icp.companySizes ?? [],
          locations: icp.locations ?? [],
        },
        account_id: linkedInAccountId,
        maxResults: 15,
      },
    })
    setDiscovering(false)

    if (!res.success) {
      setDiscoverError(res.error ?? 'Lead discovery failed. You can find leads from the Leads page after setup.')
      return
    }

    if (res.data) {
      setDiscoverResult(res.data)
      // Auto-import discovered leads into the pipeline
      if (res.data.leads.length > 0) {
        await importDiscoveredLeads(res.data.leads as ReadonlyArray<Record<string, unknown>>)
      }
    }
  }

  async function importDiscoveredLeads(leads: ReadonlyArray<Record<string, unknown>>) {
    setImporting(true)
    const leadsToImport = leads.map((lead) => ({
      linkedin_url: (lead.linkedin_url as string) ?? null,
      linkedin_id: (lead.linkedin_id as string) ?? null,
      first_name: (lead.first_name as string) ?? null,
      last_name: (lead.last_name as string) ?? null,
      full_name: (lead.full_name as string) ?? null,
      headline: (lead.headline as string) ?? null,
      company: (lead.company as string) ?? null,
      title: (lead.title as string) ?? null,
      industry: (lead.industry as string) ?? null,
      location: (lead.location as string) ?? null,
      avatar_url: (lead.avatar_url as string) ?? null,
      source: 'auto_discover',
    }))

    const res = await apiFetch<{ created: number }>('/api/leads/bulk', {
      method: 'POST',
      body: { leads: leadsToImport },
    })
    setImporting(false)

    if (res.success && res.data) {
      setImportedCount(res.data.created)
      void queryClient.invalidateQueries({ queryKey: ['leads'] })
    }
  }

  async function handleActivate() {
    setActivating(true)
    setActivateError(null)
    const res = await apiFetch<{ activated: number }>('/api/campaigns/activate-all', { method: 'POST' })
    setActivating(false)
    if (!res.success) {
      setActivateError(res.error ?? 'Activation failed. Please try again.')
      return
    }

    // Mark onboarding as complete — retry up to 3 times
    let onboardingDone = false
    for (let attempt = 0; attempt < 3 && !onboardingDone; attempt++) {
      const obRes = await apiFetch('/api/auth/onboarding-complete', { method: 'POST' })
      if (obRes.success) onboardingDone = true
    }

    if (!onboardingDone) {
      setActivateError('Setup saved but completion tracking failed. Please refresh the page.')
      setActivating(false)
      return
    }

    // Invalidate cached user-me so AccessGuard sees onboarding_completed=1
    // (it then routes a non-entitled user to /billing — the payment wall).
    await queryClient.invalidateQueries({ queryKey: ['user-me'] })

    setActivated(true)
    // Honest hand-off (2026-08-30): a brand-new account has NO campaign yet,
    // so "activate-all" activated nothing. Send them to Campaigns to create
    // their first one. The flag asks the campaigns page to open the
    // create-campaign wizard on arrival (read once, then cleared).
    try {
      sessionStorage.setItem('dca_open_wizard', '1')
    } catch {
      // sessionStorage unavailable — they still land on /campaigns
    }
    setTimeout(() => navigate('/campaigns'), 1200)
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">You're Almost There!</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Review your completed steps, then finish setup. Your first campaign is created next.
        </p>
      </div>

      <Card>
        <CardContent className="pt-4 space-y-3">
          {checklist.map(({ label, done }) => (
            <div key={label} className="flex items-center gap-3">
              <CheckCircle2
                className={['h-5 w-5 shrink-0', done ? 'text-green-500' : 'text-subtle-foreground'].join(' ')}
              />
              <span className={['text-sm', done ? '' : 'text-muted-foreground'].join(' ')}>{label}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Auto-discovery progress */}
      {discovering && (
        <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3">
          <Loader2 className="h-4 w-4 animate-spin text-cobalt-bright" />
          <span className="text-sm text-cobalt-bright">Finding leads matching your ICP...</span>
        </div>
      )}

      {importing && (
        <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3">
          <Loader2 className="h-4 w-4 animate-spin text-cobalt-bright" />
          <span className="text-sm text-cobalt-bright">Importing leads to your pipeline...</span>
        </div>
      )}

      {leadsImported && (
        <div className="flex items-center gap-3 rounded-lg border border-green-500/30 bg-green-500/5 px-4 py-3">
          <CheckCircle2 className="h-4 w-4 text-emerald-300 " />
          <span className="text-sm text-emerald-300 ">
            {importedCount} leads added to your pipeline! They'll be waiting on your dashboard.
          </span>
        </div>
      )}

      {/* Manual retry if auto-discover failed */}
      {discoverError && !discovering && linkedInAccountId && (
        <div className="space-y-2">
          <p className="text-sm text-destructive">{discoverError}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              autoDiscoverTriggered.current = false
              void runAutoDiscover()
            }}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </Button>
        </div>
      )}

      {activateError && (
        <p className="text-sm text-destructive">{activateError}</p>
      )}

      {activated ? (
        <div className="flex items-center gap-2 text-emerald-300 font-medium">
          <CheckCircle2 className="h-5 w-5" />
          You're set up. Next: create your first campaign…
        </div>
      ) : (
        <Button
          size="lg"
          className="w-full gap-2 bg-green-600 hover:bg-green-700 text-white"
          onClick={() => void handleActivate()}
          disabled={activating || discovering || importing}
        >
          {activating ? (
            <>
              <RefreshCw className="h-5 w-5 animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <Zap className="h-5 w-5" />
              Finish setup &amp; create your first campaign
            </>
          )}
        </Button>
      )}
    </div>
  )
}

// ============================================================
// Wizard shell
// ============================================================

export default function OnboardingWizard() {
  const apiFetch = useApi()
  const [step, setStep] = useState(1)
  const [linkedInConnected, setLinkedInConnected] = useState(false)
  const [linkedInAccountId, setLinkedInAccountId] = useState<string | null>(null)
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>({
    offer: '',
    target: '',
    biggestWin: '',
    offerUrl: '',
  })
  const [businessDone, setBusinessDone] = useState(false)
  const [strategyDone, setStrategyDone] = useState(false)
  const [icpDone, setIcpDone] = useState(false)
  const [urlAnalysis, setUrlAnalysis] = useState<UrlAnalysisResult | null>(null)
  const [analyzing, setAnalyzing] = useState(false)
  const [analysisError, setAnalysisError] = useState<string | null>(null)
  const [urlAnalyzed, setUrlAnalyzed] = useState(false)
  const [manualEntry, setManualEntry] = useState(false)
  const [generatedIcp, setGeneratedIcp] = useState<GeneratedIcp | null>(null)

  function goNext() {
    setStep((s) => Math.min(s + 1, TOTAL_STEPS))
  }

  // Fetch the Unipile account id when connected (needed for auto-discover).
  // 2026-09-05: this used to call /api/linkedin/accounts, a path no worker
  // serves (the gateway 404s), so linkedInAccountId stayed null for EVERY
  // new signup and step 5 silently skipped lead discovery + the first
  // campaign. /api/leads/linkedin/status is the endpoint the connect card
  // itself polls, and auto-discover wants the Unipile account id it returns.
  useEffect(() => {
    if (!linkedInConnected || linkedInAccountId) return

    async function fetchAccountId() {
      const res = await apiFetch<{ status: string; account: { unipile_account_id: string } | null }>(
        '/api/leads/linkedin/status',
      )
      const id = res.success && res.data?.status === 'connected' ? res.data.account?.unipile_account_id : undefined
      if (id) setLinkedInAccountId(id)
    }

    void fetchAccountId()
  }, [linkedInConnected, linkedInAccountId])

  // Analyze the offer URL and auto-fill business fields from results
  const handleAnalyzeUrl = useCallback(async () => {
    const url = businessInfo.offerUrl.trim()
    if (!url || analyzing) return

    // Client-side URL validation before calling API
    try {
      const parsed = new URL(url)
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        setAnalysisError('Please enter a valid URL starting with http:// or https://')
        return
      }
    } catch {
      setAnalysisError('Please enter a valid URL (e.g. https://yourcompany.com/product)')
      return
    }

    setAnalyzing(true)
    setAnalysisError(null)

    const res = await apiFetch<UrlAnalysisResult>('/api/ai/analyze-url', {
      method: 'POST',
      body: { url },
    })
    setAnalyzing(false)

    if (res.success && res.data) {
      setUrlAnalysis(res.data)

      // Auto-fill business fields from analysis
      const targetParts = [
        ...(res.data.target_audience.job_titles.slice(0, 3)),
        ...(res.data.target_audience.industries.length > 0
          ? [`in ${res.data.target_audience.industries.join(', ')}`]
          : []),
      ]
      setBusinessInfo((prev) => ({
        ...prev,
        offer: res.data!.value_proposition || prev.offer,
        target: targetParts.join(', ') || prev.target,
        biggestWin: res.data!.testimonials?.[0] || prev.biggestWin,
      }))
      setUrlAnalyzed(true)
    } else {
      setAnalysisError(res.error ?? 'URL analysis failed. You can enter details manually.')
      // Show fields for manual entry even on failure
      setUrlAnalyzed(true)
    }
  }, [businessInfo.offerUrl, analyzing, apiFetch])

  // Advance from Step 2 → Step 3, persisting offer_config
  async function handleStep2Next() {
    setBusinessDone(true)

    // Persist business info to offer_config — await to ensure save completes
    if (businessInfo.offer.trim() || businessInfo.biggestWin.trim()) {
      const offerPayload: OfferConfig = {
        ...DEFAULT_OFFER,
        offerDescription: businessInfo.offer.trim(),
        socialProof: businessInfo.biggestWin.trim(),
        coreOfferOneLiner: businessInfo.offer.trim().split('.')[0] || '',
        primaryPainPoint: urlAnalysis?.target_audience?.pain_points?.[0] || '',
        painPoints: urlAnalysis?.target_audience?.pain_points || [],
        targetAudience: businessInfo.target.trim(),
      }
      const res = await apiFetch('/api/settings/offer', { method: 'PUT', body: offerPayload })
      if (!res.success) {
        console.warn('[onboarding] Failed to persist offer config:', res.error)
      }
    }

    goNext()
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      {/* Matrix rain + grid + radial glow together match the marketing-site
          "depth stack" so onboarding feels continuous with dealcloserautopilot.com */}
      <MatrixRain />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-grid-subtle opacity-30"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-0 h-[480px] w-[640px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(28,63,255,0.22)_0%,transparent_60%)] blur-2xl"
      />

      <div className="relative flex items-start justify-center px-4 py-16">
        <div className="w-full max-w-2xl space-y-8">
          <div className="space-y-3">
            <span className="eyebrow text-[var(--color-cyan)]">// ONBOARDING</span>
            <h1 className="text-3xl font-bold tracking-tight text-[var(--color-silver-bright)] md:text-4xl">
              Welcome to{' '}
              <span className="text-gradient-cobalt">DealCloser Autopilot</span>
            </h1>
            <p className="max-w-lg text-sm leading-relaxed text-[var(--color-silver)]">
              Let&apos;s set up your account. A few quick steps and your AI sales team is running.
            </p>
          </div>

          <ProgressBar step={step} total={TOTAL_STEPS} />

          <div className="rounded-xl border border-border bg-[var(--color-card)] p-8 shadow-[0_0_40px_rgba(28,63,255,0.06)]">
          {step === 1 && (
            <StepConnectLinkedIn
              onNext={() => {
                setLinkedInConnected(true)
                goNext()
              }}
            />
          )}
          {step === 2 && (
            <StepDescribeBusiness
              info={businessInfo}
              onChange={setBusinessInfo}
              onNext={() => void handleStep2Next()}
              onSkip={goNext}
              onAnalyze={() => void handleAnalyzeUrl()}
              onManualEntry={() => setManualEntry(true)}
              analyzing={analyzing}
              analysisError={analysisError}
              urlAnalyzed={urlAnalyzed}
              manualEntry={manualEntry}
            />
          )}
          {step === 3 && (
            <StepAiReview
              info={businessInfo}
              urlAnalysis={urlAnalysis}
              onNext={() => {
                setStrategyDone(true)
                goNext()
              }}
              onSkip={goNext}
            />
          )}
          {step === 4 && (
            <StepConfigureIcp
              urlAnalysis={urlAnalysis}
              businessInfo={businessInfo}
              onIcpGenerated={setGeneratedIcp}
              onNext={() => {
                setIcpDone(true)
                goNext()
              }}
              onSkip={goNext}
            />
          )}
          {step === 5 && (
            <StepActivate
              completedSteps={{
                linkedIn: linkedInConnected,
                business: businessDone,
                strategy: strategyDone,
                icp: icpDone,
              }}
              generatedIcp={generatedIcp}
              linkedInAccountId={linkedInAccountId}
            />
          )}
          </div>
        </div>
      </div>
    </div>
  )
}
